import { MultiDatePicker } from 'components/MyDatePicker/MultiDatePicker';
import React from 'react';
import { timeInMinutes } from 'utils/date';
import cloneDeep from 'lodash/cloneDeep';
import {
  QuestionWrapper,
  QuestionBody,
  SubAnswer,
  RemoveSelectedIcon,
  SubAnswerWrapper,
  AddAnswerButton,
  DeleteIcon,
  FixedKeyVal,
} from './styles';
import Input from 'components/Input';
import TitleInput from './TitleInput';
import Select from '../Select';
import { englishNumber, isANumber } from 'utils/number';
import styles from './styles.module.css';
import FullScreenChoiceSelect from './FullScreenChoiceSelect';
import { ITags } from 'store/providers/providerTypes';
import {
  IAnswerField,
  IAnswerView,
  IMultipleAnswerAnswerView,
  IMultipleAnswerField,
  IMultipleAnswerQuestionFieldInputType,
  IMultipleAnswerQuestionView,
  IMultipleAnswerSubAnswer,
  IQuestionsView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import i18n from '../../i18n';
import Textarea from '../Textarea';

function makeId(length: number): string {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}
interface IMultipleAnswerQuestionProps {
  canChangeData: boolean;
  tags?: ITags['data'];
  answer?: IMultipleAnswerAnswerView;
  isRequired: boolean;
  disabled?: boolean;
  onChange: (newAnswer: any, questionId: number) => void;
  question: IMultipleAnswerQuestionView;
  survey?: ISurveyView;
  draft?: boolean;
  answers?: { [key: number]: IAnswerView };
  questions?: { [questionId: number]: IQuestionsView };
}

interface IMultipleAnswerQuestionState {
  viewChoices: boolean;
}

const inputPatterns: {
  [type in IMultipleAnswerQuestionFieldInputType]?: string;
} = {
  text: undefined,
  date: undefined,
  duration: undefined,
  number: '([0-9۰-9])*',
  time: undefined,
};

const inputTypes: {
  [type in Exclude<
    IMultipleAnswerQuestionFieldInputType,
    'date' | 'time' | 'duration'
  >]: 'number' | 'text';
} = {
  text: 'text',
  number: 'text',
};

const inputModes: {
  [mode in IMultipleAnswerQuestionFieldInputType]?: 'tel' | 'text' | 'numeric';
} = {
  text: 'text',
  duration: 'text',
  date: 'text',
  number: 'text',
};

class MultipleAnswerQuestion extends React.Component<
  IMultipleAnswerQuestionProps,
  IMultipleAnswerQuestionState
> {
  constructor(props: IMultipleAnswerQuestionProps) {
    super(props);
    this.state = { viewChoices: false };
  }

  handleChange = (value: any, name: string) => {
    const newAnswer = { ...this.props.answer, [name]: value };
    this.props.onChange(newAnswer, this.props.question.id);
  };

  setDate = (_type: string, value: string) => {
    this.handleChange(
      (Number(value) + 12 * 60 * 60 * 1000).toString(),
      'value'
    );
  };

  setTime = (value: number) => {
    const minutes = timeInMinutes(value);
    this.handleChange(minutes.toString(), 'value');
  };

  preventWhiteSpace = (e: React.KeyboardEvent) => {
    if (e.which === 32 || e.key === 'Enter') {
      e.preventDefault();
      return false;
    }
    return;
  };
  preventSubmitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      return false;
    }
    return;
  };

  // shouldComponentUpdate(
  //   nextProps: Readonly<IMultipleAnswerQuestionProps>,
  //   nextState: Readonly<IMultipleAnswerQuestionState>
  // ): boolean {}

  addAnswers = () => {
    if (
      this.props.question?.maxAnswers &&
      this.props.question.questionFields[0].options === null &&
      (this.props.answer?.subAnswerList?.length || 0) >=
        this.props.question.maxAnswers
    )
      return;

    if (this.props.question.questionFields[0].options !== null) {
      this.setState({ viewChoices: true });
      return;
    }
    const newAnswer = cloneDeep(this.props.answer) || {
      subAnswerList: [] as IMultipleAnswerSubAnswer[],
    };
    const lastOrder =
      newAnswer.subAnswerList.length > 0
        ? newAnswer.subAnswerList[0].order || 0
        : 0;
    newAnswer.subAnswerList.push({
      ref: makeId(10),
      order: lastOrder + 1,
      implicit: false,
      values: null,
    });
    this.props.onChange(newAnswer, this.props.question.id);
  };

  setValue = (index: number, key: string, value: string | null) => {
    const newAnswer = cloneDeep(this.props.answer) || {
      subAnswerList: [] as IMultipleAnswerSubAnswer[],
    };
    const fieldIndex =
      newAnswer.subAnswerList[index].values &&
      newAnswer.subAnswerList[index].values!.findIndex(
        (fieldAnswer: IAnswerField) => {
          return fieldAnswer.key === key;
        }
      );
    if (isANumber(fieldIndex) && fieldIndex! >= 0) {
      newAnswer.subAnswerList[index].values![Number(fieldIndex)].value = value;
    } else {
      if (newAnswer.subAnswerList[index].values === null) {
        newAnswer.subAnswerList[index].values = [];
      }
      newAnswer.subAnswerList[index].values!.push({
        key: key,
        value: value,
      } as IAnswerField);
    }
    this.props.onChange(newAnswer, this.props.question.id);
  };
  onHeadChoicesSelect = (choices: string[]) => {
    this.setState({ viewChoices: false });
    const headKey = this.props.question.questionFields[0].key;
    const newAnswer = cloneDeep(this.props.answer) || {
      subAnswerList: [] as IMultipleAnswerSubAnswer[],
    };
    newAnswer.subAnswerList = newAnswer.subAnswerList.filter(
      (s: IMultipleAnswerSubAnswer) => {
        if (!s.values) return false;
        const answerField = s.values.find((v) => v.key === headKey);
        if (!answerField || !answerField.value) return false;
        if (!choices.includes(answerField.value!)) return false;
        choices.splice(choices.indexOf(answerField.value!), 1);
        return true;
      }
    );
    let lastOrder =
      newAnswer.subAnswerList.length > 0
        ? newAnswer.subAnswerList[0].order || 0
        : 0;
    choices.forEach((newChoice) => {
      const field = { key: headKey, value: newChoice } as IAnswerField;
      newAnswer.subAnswerList.push({
        ref: makeId(10),
        order: ++lastOrder,
        implicit: false,
        values: [field],
      });
    });
    this.props.onChange(newAnswer, this.props.question.id);
  };

  removeSubAnswer = (index: number) => {
    const newAnswer = cloneDeep(this.props.answer) as IMultipleAnswerAnswerView;
    newAnswer.subAnswerList.splice(index, 1);
    this.props.onChange(newAnswer, this.props.question.id);
  };
  render() {
    const { question, answer, disabled, canChangeData, tags, survey, draft } =
      this.props;

    const showFullscreen =
      question?.fields?.find((f) => f.name === 'conf_fullScreen')?.value ===
      'true';

    const { viewChoices } = this.state;
    let choicePicker: any = undefined;
    if (viewChoices) {
      const choices = question.questionFields[0].options || [];
      const key = question.questionFields[0].key;
      const selected =
        answer &&
        answer.subAnswerList.map((sa) => {
          try {
            return sa.values!.find((e) => e.key === key)!.value!;
          } catch {
            return '';
          }
        });
      choicePicker = (
        <FullScreenChoiceSelect
          choices={choices.map((it) => ({ value: it, title: it }))}
          title={`${question.title} - ${question.questionFields[0].title}`}
          selected={selected || ([] as string[])}
          maxCount={question.maxAnswers}
          onSelect={this.onHeadChoicesSelect}
        />
      );
    }

    return (
      <QuestionWrapper disabled={disabled}>
        {viewChoices && choicePicker}
        <TitleInput
          fields={question.fields}
          hint={question.hint}
          required={question.answerRequired}
          title={question.title}
          tags={question.tags}
          allTags={tags}
          indexNumber={question.indexOrder}
          survey={survey}
          answers={this.props.answers}
          questions={this.props.questions}
        />
        <QuestionBody style={{ overflow: 'visible' }}>
          <div className="row">
            {answer &&
              answer.subAnswerList.map(
                (subAnswer: IMultipleAnswerSubAnswer, answerIndex: number) => {
                  return (
                    <SubAnswerWrapper
                      className={
                        showFullscreen ? 'col m12 l12 s12' : 'col m6 l6 s12'
                      }
                      key={question.id + '_' + answerIndex}
                    >
                      <DeleteIcon
                        type={'minus'}
                        onClick={() => {
                          this.removeSubAnswer(answerIndex);
                        }}
                      />
                      <SubAnswer>
                        {question.questionFields.map(
                          (
                            questionField: IMultipleAnswerField,
                            fieldIndex: number
                          ) => {
                            if (questionField.hidden) return <></>;

                            if (questionField.viewCondition?.refQuestionPart) {
                              const conditionQuestionKey =
                                questionField.viewCondition?.refQuestionPart;
                              const fieldValues =
                                questionField.viewCondition?.questionValues;
                              const foundedField = (
                                answer.subAnswerList[answerIndex].values || []
                              ).find(
                                (el: IAnswerField) =>
                                  el.key === conditionQuestionKey
                              );

                              const isEnabled =
                                foundedField &&
                                fieldValues?.includes(foundedField.value || '');
                              if (!isEnabled) return <></>;
                            }

                            const item =
                              subAnswer.values &&
                              subAnswer.values.find(
                                (item) => item.key === questionField.key!
                              );
                            const value = (item && item.value) || undefined;
                            const protectedFromThisUser =
                              questionField.protection && !canChangeData;
                            if (!value && protectedFromThisUser) return <></>;
                            if (questionField.options) {
                              if (fieldIndex === 0) {
                                return (
                                  <FixedKeyVal key={fieldIndex}>
                                    {(questionField.title || '') + ' : '}
                                    <span>{value}</span>
                                  </FixedKeyVal>
                                );
                              }
                              if (questionField.popup)
                                return (
                                  <Select
                                    label={questionField.title || ''}
                                    key={
                                      question.id +
                                      '_' +
                                      answerIndex +
                                      '_' +
                                      questionField.key
                                    }
                                    isRequired={
                                      draft ? false : questionField.required
                                    }
                                    disabled={
                                      fieldIndex === 0 || protectedFromThisUser
                                    }
                                    onChangeValues={(values) =>
                                      this.setValue(
                                        answerIndex,
                                        questionField.key!,
                                        values.length === 0
                                          ? null
                                          : values[0]!.value.toString()
                                      )
                                    }
                                    options={questionField.options.map(
                                      (item) => {
                                        return {
                                          label: item,
                                          value: item,
                                        };
                                      }
                                    )}
                                    values={
                                      value
                                        ? [
                                            {
                                              label: value,
                                              value: value,
                                            },
                                          ]
                                        : []
                                    }
                                  />
                                );
                              else
                                return (
                                  <div
                                    key={`${question.id}_${answerIndex}_${questionField.key}`}
                                  >
                                    <label style={{ display: 'block' }}>
                                      {questionField.title}
                                    </label>
                                    {questionField.options.map(
                                      (option: string) => {
                                        return (
                                          <span
                                            key={`${question.id}_${answerIndex}_${questionField.key}_${option}`}
                                            className="container"
                                          >
                                            <input
                                              id={`${question.id}_${answerIndex}_${questionField.key}_${option}`}
                                              name={`${question.id}_${answerIndex}_${questionField.key}`}
                                              type="radio"
                                              disabled={protectedFromThisUser}
                                              value={option}
                                              checked={value === option}
                                              required={
                                                draft
                                                  ? false
                                                  : questionField.required
                                              }
                                              onChange={(e) => {
                                                if (e.target.checked)
                                                  this.setValue(
                                                    answerIndex,
                                                    questionField.key!,
                                                    option
                                                  );
                                              }}
                                              className="with-gap"
                                              style={{
                                                position: 'initial',
                                                width: '1px',
                                                height: '1px',
                                              }}
                                            />
                                            <label
                                              htmlFor={`${question.id}_${answerIndex}_${questionField.key}_${option}`}
                                              className={styles.checkboxLabel}
                                            >
                                              {option}
                                            </label>
                                            {option === value &&
                                              !protectedFromThisUser && (
                                                <RemoveSelectedIcon
                                                  onClick={() =>
                                                    this.setValue(
                                                      answerIndex,
                                                      questionField.key!,
                                                      null
                                                    )
                                                  }
                                                  type="x"
                                                />
                                              )}
                                          </span>
                                        );
                                      }
                                    )}{' '}
                                  </div>
                                );
                            } else if (questionField.inputType === 'date')
                              return (
                                <MultiDatePicker
                                  key={`${question.id}`}
                                  id={
                                    question.id +
                                    '_' +
                                    answerIndex +
                                    '_' +
                                    questionField.key
                                  }
                                  value={Number(value) ?? null}
                                  label={questionField.title || ''}
                                  isEditable={!protectedFromThisUser}
                                  required={questionField.required}
                                  setDate={(value) => {
                                    this.setValue(
                                      answerIndex,
                                      questionField.key!,
                                      value
                                        ? (
                                            Number(value) +
                                            12 * 60 * 60 * 1000
                                          ).toString()
                                        : null
                                    );
                                  }}
                                />
                              );
                            else if (questionField.inputType === 'text')
                              return (
                                <Textarea
                                  label={questionField.title || ''}
                                  key={
                                    question.id +
                                    '_' +
                                    answerIndex +
                                    '_' +
                                    questionField.key
                                  }
                                  id={questionField.key + 'input'}
                                  disabled={protectedFromThisUser}
                                  required={questionField.required}
                                  value={value || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    this.setValue(
                                      answerIndex,
                                      questionField.key!,
                                      value
                                    );
                                  }}
                                />
                              );
                            else {
                              const isOnlyNumber =
                                questionField.inputType === 'number';
                              return (
                                <Input
                                  label={questionField.title || ''}
                                  key={
                                    question.id +
                                    '_' +
                                    answerIndex +
                                    '_' +
                                    questionField.key
                                  }
                                  id={questionField.key + 'input'}
                                  pattern={
                                    inputPatterns[
                                      questionField.inputType || 'text'
                                    ]
                                  }
                                  inputMode={
                                    inputModes[
                                      questionField.inputType || 'text'
                                    ]
                                  }
                                  type={
                                    inputTypes[
                                      questionField.inputType || 'text'
                                    ]
                                  }
                                  step="any"
                                  onKeyPress={
                                    isOnlyNumber
                                      ? this.preventWhiteSpace
                                      : this.preventSubmitOnEnter
                                  }
                                  disabled={protectedFromThisUser}
                                  required={
                                    draft ? false : questionField.required
                                  }
                                  value={value || ''}
                                  onChange={(e) => {
                                    let value = e.target.value;
                                    if (isOnlyNumber) {
                                      value = englishNumber(
                                        e.target.value || '0'
                                      );
                                      if (isNaN(Number(value))) return;
                                    }
                                    this.setValue(
                                      answerIndex,
                                      questionField.key!,
                                      value
                                    );
                                  }}
                                />
                              );
                            }
                          }
                        )}
                      </SubAnswer>
                    </SubAnswerWrapper>
                  );
                }
              )}
            {!disabled && (
              <AddAnswerButton style={{ display: 'flex' }}>
                <div onClick={this.addAnswers} style={{ display: 'flex' }}>
                  <div>{i18n.t('add', { ns: 'survey' }) as string}</div>
                  <div style={{ position: 'relative' }}>
                    {question.answerRequired &&
                      (!answer ||
                        !answer!.subAnswerList ||
                        answer!.subAnswerList!.length === 0) && (
                        <Input
                          hidden={true}
                          key={question.id + 'placeholder'}
                          id={question.id + 'placeholder'}
                          name={question.id + 'placeholder'}
                          type="text"
                          required={!draft}
                          style={{
                            width: '3rem',
                            height: 0,
                            position: 'absolute',
                            zIndex: -1,
                          }}
                          value=""
                        />
                      )}
                  </div>
                </div>
              </AddAnswerButton>
            )}
          </div>
        </QuestionBody>
      </QuestionWrapper>
    );
  }
}

export default MultipleAnswerQuestion;
